import React, { useState, useCallback } from 'react'
import SearchComponent from '../components/SearchComponent';
import { Dropdown } from 'react-bootstrap';
import AdminLayout from '../../../layouts/AdminLayout';
import CreateInventoryDialog from '../components/CreateInventoryDialog';
import useIndexInventory from '../../../services/inventory/hooks/useIndexInventory';
import { useDeleteInventory } from '../../../services/inventory/hooks/useDeleteInventory';
import { InventoryData } from '../../../services/inventory/response/IndexInventoryResponse';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import EditInventoryDialog from '../components/EditInventoryDialog';
import PaginationComponent from '../../../shared/components/pagination/PaginationComponent';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import ReadOnlyGuard from '../../../shared/components/ReadOnlyGuard';
import PropertyInactiveBanner from '../../../shared/components/PropertyInactiveBanner';

const InventoryPage: React.FC = () => {
    const [page, setPage] = useState<number>(1);
    const [searchQuery, setSearchQuery] = useState<string>('');

    const [show, setShow] = useState<boolean>(false);

    const [editState, setEditState] = useState<boolean>(false);
    const [inventoryIdWillEdit, setInventoryIdWillEdit] = useState<string>('');

    const { data: inventoriesData, isSuccess, isError, isLoading } = useIndexInventory({
        search: searchQuery,
        page: page,
        limit: 10,
    });

    const { mutate, isPending } = useDeleteInventory();

    const queryClient = useQueryClient();

    const onRemove = useCallback((e: React.MouseEvent, data: InventoryData) => {
        e.preventDefault();
        e.stopPropagation();

        if (isPending) return;

        toast.loading('Removing inventory...', { id: 'remove-inventory' });

        mutate({ id: data.id }, {
            onSuccess: async () => {
                await queryClient.refetchQueries({
                    predicate: (query) => query.queryKey.some((key) => ['inventories'].includes(key as string))
                });

                toast.success('Inventory removed successfully', { id: 'remove-inventory' });
            },
            onError: (mutationError) => {
                toast.error(mutationError?.message || 'Failed to remove inventory', { id: 'remove-inventory' });
            }
        });
    }, [mutate, isPending, queryClient]);

    const handleOpenUpdate = useCallback((e: React.MouseEvent, inventoryId: string) => {
        e.preventDefault();
        e.stopPropagation();
        setInventoryIdWillEdit(inventoryId);
        setEditState(true);
    }, []);

    const handleCloseUpdate = () => {
        setInventoryIdWillEdit('');
        setEditState(false);
    }

    return (
        <AdminLayout>
            <div className="container-fluid mt-5">
                <div className="table-container min-vh-100">
                    <h3 className="mb-3">Inventory</h3>
                    <PropertyInactiveBanner />
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center mb-3 gap-3">
                        <SearchComponent queryKey={searchQuery} onSearch={setSearchQuery} />
                        <RequireAuth requiredRoles={['dev']} requiredPermissions={['inventory_create']} asComponent>
                            <ReadOnlyGuard>
                                <button className="btn btn-primary" onClick={() => setShow(true)}>
                                    Create
                                </button>
                            </ReadOnlyGuard>
                        </RequireAuth>
                    </div>
                    <div className="inventory-table-wrapper">
                        <table className="table table-striped d-none d-md-table">
                            <thead>
                                <tr>
                                    <th>Name</th>
                                    <th className='text-end'>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {isError && <tr><td colSpan={2}>Failed to load data.</td></tr>}
                                {isLoading && <tr><td colSpan={2}>Loading...</td></tr>}
                                {isSuccess && !isLoading && inventoriesData.data.length > 0 && inventoriesData.data.map((inventory) => (
                                    <tr key={inventory.id}>
                                        <td>{inventory.item_name}</td>
                                        <td className="text-end">
                                            <Dropdown>
                                                <Dropdown.Toggle variant="default" id={`dropdownMenuButton-${inventory.id}`}>
                                                    Actions
                                                </Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['inventory_update']} asComponent>
                                                        <ReadOnlyGuard mode="hide">
                                                            <Dropdown.Item onClick={(e) => handleOpenUpdate(e, inventory.id)}>Edit</Dropdown.Item>
                                                        </ReadOnlyGuard>
                                                    </RequireAuth>
                                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['inventory_delete']} asComponent>
                                                        <ReadOnlyGuard mode="hide">
                                                            <Dropdown.Item onClick={(e) => onRemove(e, inventory)} className='text-danger' disabled={isPending}>
                                                                Remove
                                                            </Dropdown.Item>
                                                        </ReadOnlyGuard>
                                                    </RequireAuth>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </td>
                                    </tr>
                                ))}
                                {isSuccess && !isLoading && inventoriesData.data.length === 0 && (
                                    <tr><td colSpan={2} className="text-center">No data available.</td></tr>
                                )}
                            </tbody>
                        </table>

                        {/* Tampilan Card untuk Layar Mobile */}
                        <div className="d-md-none">
                            {isError && <div className="text-center my-4">Failed to load data.</div>}
                            {isLoading && <div className="text-center my-4">Loading...</div>}
                            {isSuccess && !isLoading && inventoriesData.data.length > 0 && inventoriesData.data.map((inventory) => (
                                <div key={inventory.id} className="card mb-3">
                                    <div className="card-body">
                                        <h5 className="card-title">{inventory.item_name}</h5>
                                        <div className="d-flex justify-content-end mt-3">
                                            <Dropdown>
                                                <Dropdown.Toggle variant="default" id={`dropdownMenuButton-mobile-${inventory.id}`}>Actions</Dropdown.Toggle>
                                                <Dropdown.Menu>
                                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['inventory_update']} asComponent>
                                                        <ReadOnlyGuard mode="hide">
                                                            <Dropdown.Item onClick={(e) => handleOpenUpdate(e, inventory.id)}>Edit</Dropdown.Item>
                                                        </ReadOnlyGuard>
                                                    </RequireAuth>
                                                    <RequireAuth requiredRoles={['dev']} requiredPermissions={['inventory_delete']} asComponent>
                                                        <ReadOnlyGuard mode="hide">
                                                            <Dropdown.Item onClick={(e) => onRemove(e, inventory)} className='text-danger' disabled={isPending}>Remove</Dropdown.Item>
                                                        </ReadOnlyGuard>
                                                    </RequireAuth>
                                                </Dropdown.Menu>
                                            </Dropdown>
                                        </div>
                                    </div>
                                </div>
                            ))}
                            {isSuccess && !isLoading && inventoriesData.data.length === 0 && (
                                <div className="text-center my-4">No data available.</div>
                            )}
                        </div>
                    </div>
                    {isSuccess && !isLoading && inventoriesData.data.length !== 0 && inventoriesData.pagination && (
                        <PaginationComponent
                            totalPages={inventoriesData.pagination.last_page}
                            currentPage={inventoriesData.pagination.current_page}
                            onPageChange={(page) => setPage(page)}
                        />
                    )}
                </div>
            </div>
            <RequireAuth requiredRoles={['dev']} requiredPermissions={['inventory_create']} asComponent>
                <ReadOnlyGuard mode="hide">
                    <CreateInventoryDialog show={show} onClose={() => setShow(false)} />
                </ReadOnlyGuard>
            </RequireAuth>
            {editState && inventoryIdWillEdit && <RequireAuth requiredRoles={['dev']} requiredPermissions={['inventory_update']} asComponent><ReadOnlyGuard mode="hide"><EditInventoryDialog show={editState} onClose={handleCloseUpdate} inventoryId={inventoryIdWillEdit} /></ReadOnlyGuard></RequireAuth>}
        </AdminLayout>
    )
}

export default InventoryPage
